"use client";

import Link from "next/link";
import { GradientButton } from "@/components/core/navigation/Navigation";
import { TrackedSignUpButton } from "@/components/shared/signup/TrackedSignUp";
import { useLoggedIn } from "@/helpers/isLoggedIn";

export type CtaSectionGrayBackgroundProps = {
  title?: string;
  titleNode?: React.ReactNode;
  description?: string;
  disclaimerText?: string;
  /** Hide the primary action for legacy sections without a complete button. */
  showPrimaryButton?: boolean;
  primaryLabel?: string;
  primaryCtaType?: "link" | "modal";
  primaryLink?: string;
  secondaryLabel?: string;
  secondaryCtaType?: "link" | "modal";
  secondaryLink?: string;
};

const ModalGradientButton = ({ label, className }: { label: string; className?: string }) => {
  const loggedIn = useLoggedIn();
  const cls = `relative rounded h-10 items-center text-center px-4 leading-none group font-medium bg-[#FD8C66] hover:bg-[#F8494D] transition-colors duration-1000 shrink-0 cursor-pointer ${className ?? ""}`;
  const inner = (
    <>
      <span className="relative z-10 whitespace-nowrap">{loggedIn ? "Open Dash0" : label}</span>
      <div className="absolute pointer-events-none inset-0 overflow-hidden rounded">
        <div className="animate-random-bubble gradient-bubble absolute" />
      </div>
    </>
  );
  if (loggedIn) return <Link href="https://app.dash0.com" className={cls}>{inner}</Link>;
  return (
    <TrackedSignUpButton placement="cta" mode="modal" forceRedirectUrl="https://app.dash0.com" fallbackRedirectUrl="https://app.dash0.com">
      <button className={cls}>{inner}</button>
    </TrackedSignUpButton>
  );
};

const ModalSecondaryButton = ({ label }: { label: string }) => {
  const loggedIn = useLoggedIn();
  const cls = "bg-white text-grey-950 hover:bg-grey-200 rounded h-10 items-center text-center py-4 min-h-10 px-5 min-w-[145px] justify-center font-medium transition-colors duration-200 flex whitespace-nowrap cursor-pointer";
  if (loggedIn) return <Link href="https://app.dash0.com" className={cls}>Open Dash0</Link>;
  return (
    <TrackedSignUpButton placement="cta" mode="modal" forceRedirectUrl="https://app.dash0.com" fallbackRedirectUrl="https://app.dash0.com">
      <button className={cls}>{label}</button>
    </TrackedSignUpButton>
  );
};

export default function CtaSectionGrayBackground({
  title = "Ready to take your observability to the next level? Get started with Dash0 today!",
  titleNode,
  description,
  disclaimerText,
  showPrimaryButton = true,
  primaryLabel = "Getting started",
  primaryCtaType = "link",
  primaryLink = "/sign-up",
  secondaryLabel,
  secondaryCtaType = "link",
  secondaryLink,
}: CtaSectionGrayBackgroundProps) {
  const hasExtra = !!description || !!disclaimerText;
  const wrapperPadding = hasExtra ? "py-10 md:py-16" : "py-10 md:py-20";
  const titleMaxWidth = hasExtra ? "max-w-6xl" : "max-w-2xl";
  const descriptionMaxWidth = hasExtra ? "max-w-4xl" : "max-w-2xl";
  return (
    <section data-signup-placement="cta" className="pb-10 md:pb-10">
      <div className={`bg-grey-900 rounded-lg px-6 text-center ${wrapperPadding}`}>
        <h2 className={`text-[32px] font-medium leading-[100%] tracking-[0%] text-white text-center mx-auto mt-0 mb-8 ${titleMaxWidth}`}>
          {titleNode ?? title}
        </h2>
        {description && (
          <p className={`text-grey-400 text-[17px] font-normal leading-[120%] md:leading-[180%] tracking-[0%] text-center mx-auto mt-0 mb-8 ${descriptionMaxWidth}`}>
            {description}
          </p>
        )}
        <div className="flex flex-wrap gap-3 items-center justify-center">
          {showPrimaryButton && (primaryCtaType === "modal" ? (
            <ModalGradientButton
              label={primaryLabel ?? ""}
              className="py-3 min-h-10 px-5 min-w-[145px] inline-flex justify-center"
            />
          ) : (
            <GradientButton className="py-3 min-h-10 px-5 min-w-[145px] justify-center" href={primaryLink}>
              {primaryLabel}
            </GradientButton>
          ))}

          {secondaryLabel && (
            secondaryCtaType === "modal" ? (
              <ModalSecondaryButton label={secondaryLabel} />
            ) : secondaryLink ? (
              <Link
                href={secondaryLink}
                className="bg-white text-grey-950 hover:bg-grey-200 rounded h-10 items-center text-center py-4 min-h-10 px-5 min-w-[145px] justify-center font-medium transition-colors duration-200 flex whitespace-nowrap"
              >
                {secondaryLabel}
              </Link>
            ) : null
          )}
        </div>
        {disclaimerText && (
          <p className="text-grey text-[14px] font-normal leading-xxl tracking-[0%] text-center max-w-md mx-auto mt-6 mb-0">
            {disclaimerText}
          </p>
        )}
      </div>
    </section>
  );
}
